import { Button, Spinner } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';
import CreatorCard from '../components/CreatorCard';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import { Creator } from '../types/creators';
import { Product } from '../types/product';
import { generateCanonicalUrl } from '../utils/seoUtils';

const Home: React.FC = () => {
  const { currentLocale } = useLocale();
  const countryCode = currentLocale.preferredCountryCode;

  const {
    data: featuredProducts,
    isLoading,
    error,
  } = useQuery<Product[]>({
    queryKey: ['featuredProducts', countryCode],
    queryFn: () => productService.getFeaturedProducts(countryCode),
  });

  const { data: creators, isLoading: creatorsLoading } = useQuery({
    queryKey: ['creators'],
    queryFn: () => productService.getCreators(),
  });

  return (
    <>
      <SEOHead
        title='Premium 3D Printed Miniatures for Tabletop Gaming'
        description='Discover CodedGhost Minis - Premium 3D printed miniatures for tabletop gaming, D&D campaigns, and miniature painting. High-quality prints from talented creators worldwide.'
        keywords='3D printed miniatures, tabletop gaming, D&D miniatures, miniature painting, Warhammer alternatives, custom miniatures, fantasy miniatures, sci-fi miniatures'
        url={generateCanonicalUrl('/')}
        type='website'
      />

      {/* Hero Section */}
      <section className='bg-gradient-to-br from-primary-500 to-primary-700 text-white py-16 lg:py-24'>
        <div className='max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8'>
          <h1 className='text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6'>
            Welcome to CodedGhostMinis
          </h1>
          <p className='text-xl lg:text-2xl mb-8 opacity-90 max-w-3xl mx-auto'>
            Discover premium 3D printed miniatures for tabletop gaming. From
            fantasy heroes to sci-fi warriors, bring your campaigns to life.
          </p>
          <Button
            as={Link}
            to='/search'
            color='secondary'
            variant='solid'
            size='lg'
            className='text-lg px-8 py-4'
          >
            Browse Miniatures
          </Button>
        </div>
      </section>

      {/* Featured Section */}
      <section className='py-16 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-12'>
            Featured Miniatures
          </h2>

          {isLoading && (
            <div className='flex justify-center items-center py-12'>
              <Spinner size='lg' color='primary' />
              <span className='ml-3 text-gray-600'>
                Loading featured miniatures...
              </span>
            </div>
          )}

          {error && (
            <div className='text-center py-12'>
              <p className='text-red-600 text-lg'>
                Failed to load featured miniatures. Please try again later.
              </p>
            </div>
          )}

          {featuredProducts && featuredProducts.length > 0 && (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
              {featuredProducts.map((product: Product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          {featuredProducts && featuredProducts.length === 0 && (
            <div className='text-center py-12'>
              <p className='text-gray-600 text-lg'>
                No featured miniatures available at the moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Licensed Creators Section */}
      <section className='py-16 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4'>
            Licensed Creators
          </h2>
          <p className='text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto'>
            Discover miniatures from our talented licensed creators. Each brings
            their unique style and vision to the world of tabletop gaming.
          </p>

          {creatorsLoading && (
            <div className='flex justify-center items-center py-12'>
              <Spinner size='lg' color='primary' />
              <span className='ml-3 text-gray-600'>Loading creators...</span>
            </div>
          )}

          {creators && creators.length > 0 && (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {creators.slice(0, 6).map((creator: Creator) => (
                <CreatorCard key={creator.id} creator={creator} />
              ))}
            </div>
          )}

          {creators && creators.length > 6 && (
            <div className='text-center mt-8'>
              <Button
                as={Link}
                to='/search'
                color='primary'
                variant='bordered'
                size='lg'
              >
                View All Creators
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Home;
